// src/pages/settings/team/TeamTab.tsx
//
// S5 (ClickUp 86d42twq8) — converted from the standalone `/settings/team` route
// (`TeamPage.tsx`) into the settings drawer's CLINIC group. `Layout` and `PageContainer` are
// stripped; the rail names the tab, so the page title and subtitle go with them.
//
// 🚨 The seat-limit CTA below still `navigate()`s to `/help` from inside the drawer. That is
// deliberate: D7 (`SettingsModal`'s close-on-pathname-change effect) closes the drawer behind
// it, which is the wanted behaviour — no explicit `closeSettings()` call here.
import React, { useState, useEffect } from "react";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnsType } from "antd/es/table";
import { Typography } from "antd";
import { TeamOutlined } from "@ant-design/icons";

import PageToolbar from "../../../components/shared/PageToolbar";
import DataTable from "../../../components/shared/DataTable";
import StatusTag from "../../../components/shared/StatusTag";
import EditUserModal from "../../../components/editUserModal/EditUserModal";

import { fetchTeamMembers, fetchSeatUsage } from "../../../api/admin/teamApi";
import { defaultTeamMember, TeamMember } from "../../../types/admin/TeamTypes";
import { loadUserPreferences } from "../../../utils/userPrefs/UserPrefs";

const { Text, Link } = Typography;

const TEAM_MEMBERS_PER_PAGE = loadUserPreferences().tableRowsPerPage;

const TeamTab: React.FC = () => {
	const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
	const [totalTeamMembers, setTotalTeamMembers] = useState<number>(0);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember | null>(null);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState("");

	const navigate = useNavigate();
	const queryClient = useQueryClient();

	// Seat usage is read from the server, never derived here — the same service backs the
	// creation gate, so the counter cannot drift from what the backend will actually allow.
	const { data: seatUsage } = useQuery({
		queryKey: ["seat-usage"],
		queryFn: fetchSeatUsage,
		retry: false,
	});

	useEffect(() => { loadTeamMembers(); }, [currentPage]);

	const loadTeamMembers = async (search: string = searchTerm) => {
		setIsLoading(true);
		const user = getSessionUser();
		try {
			const { data, total } = await fetchTeamMembers(
				currentPage,
				TEAM_MEMBERS_PER_PAGE,
				search
			);
			setTeamMembers(data);
			setTotalTeamMembers(total);
		} catch (err) {
			console.error("Error fetching team members:", err);
		} finally {
			setIsLoading(false);
		}
	};

	const handleSearch = (value: string) => {
		setSearchTerm(value);
		setCurrentPage(1);
		loadTeamMembers(value);
	};

	const handleReset = () => {
		setSearchTerm("");
		setCurrentPage(1);
		loadTeamMembers("");
	};

	const columns: ColumnsType<TeamMember> = [
		{
			title: "First Name", dataIndex: "first_name", key: "first_name", width: 150,
			sorter: (a, b) => (a.first_name || "").localeCompare(b.first_name || ""),
		},
		{
			title: "Last Name", dataIndex: "last_name", key: "last_name", width: 150,
			sorter: (a, b) => (a.last_name || "").localeCompare(b.last_name || ""),
		},
		{
			title: "Email", dataIndex: "email", key: "email", width: 220,
			sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
		},
		{ title: "Phone", dataIndex: "phone", key: "phone", width: 140 },
		{
			title: "Specialties", key: "specialties", width: 220,
			render: (_: any, record: TeamMember) => {
				const specialties = record.team_member_info?.specialties || [];
				if (!specialties.length) return <span style={{ color: "var(--mg-text-disabled)" }}>—</span>;
				return (
					<div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
						{specialties.slice(0, 2).map((s, i) => <StatusTag key={i} status={s} label={s} />)}
						{specialties.length > 2 && (
							<span style={{ fontSize: 12, color: "var(--mg-text-secondary)" }}>+{specialties.length - 2} more</span>
						)}
					</div>
				);
			},
		},
	];

	// A failed/absent seat query degrades to "unenforced" — the page must stay usable, and the
	// backend still gates creation regardless of what the frontend shows.
	const seatsEnforced = seatUsage?.enforced === true;
	const atSeatLimit = seatsEnforced && seatUsage.used >= seatUsage.total;

	return (
		<>
			<PageToolbar
				primaryAction={{
					label: "Add Team Member",
					onClick: () => {
						setSelectedTeamMember(defaultTeamMember);
						setIsModalOpen(true);
					},
					disabled: atSeatLimit,
				}}
				filters={[
					{
						type: "search",
						placeholder: "Search team members…",
						value: searchTerm,
						onChange: handleSearch,
						width: 260,
					},
					...(seatsEnforced ? [{
						type: "custom" as const,
						render: (
							<span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
								<Text
									data-testid="team-seat-counter"
									type={atSeatLimit ? "danger" : "secondary"}
									style={{ fontSize: 13, whiteSpace: "nowrap" as const }}
								>
									<TeamOutlined style={{ marginRight: 6 }} />
									{seatUsage?.used} / {seatUsage?.total} seats used
								</Text>
								{atSeatLimit && (
									<Text type="danger" style={{ fontSize: 13, whiteSpace: "nowrap" as const }}>
										Seat limit reached —{" "}
										<Link data-testid="team-seat-cta" onClick={() => navigate("/help")}>
											Contact support to add seats
										</Link>
									</Text>
								)}
							</span>
						),
					}] : []),
				]}
				onReset={handleReset}
			/>
			<DataTable
				columns={columns}
				dataSource={teamMembers}
				rowKey="id"
				loading={isLoading}
				onRowClick={(member) => { setSelectedTeamMember(member); setIsModalOpen(true); }}
				total={totalTeamMembers}
				currentPage={currentPage}
				pageSize={TEAM_MEMBERS_PER_PAGE}
				onPageChange={(page) => setCurrentPage(page)}
				emptyTitle="No team members found"
				emptyDescription="Add a team member to get started"
				fillHeight
			/>
			{selectedTeamMember && (
				<EditUserModal
					isOpen={isModalOpen}
					onClose={() => { setSelectedTeamMember(null); setIsModalOpen(false); }}
					user={selectedTeamMember}
					mode="team"
					onSaved={async () => {
						setIsModalOpen(false);
						setSelectedTeamMember(null);
						await loadTeamMembers();
						// Adding or deactivating a member moves the server-side count — refetch it
						// rather than leaving a stale quota on screen.
						queryClient.invalidateQueries({ queryKey: ["seat-usage"] });
					}}
				/>
			)}
		</>
	);
};

export default TeamTab;
